import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import Select from "../../../shared/ui/Field/Select";
import Textarea from "../../../shared/ui/Field/Textarea";
import InlineError from "../../../shared/ui/Field/InlineError";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import Text from "../../../shared/ui/Typography/Text";

import { fetchClientById, patchClientById } from "../api/clients";
import {
  parseDrfFieldErrors,
  messageFromError,
} from "../../../shared/api/drfErrors";

// ClientEditForm — feature widget. Owns local state for the Client
// core PATCH lifecycle: load, edit, validate, submit, sync.
// No Redux slice — Client core data is not consumed cross-feature.
//
// Mature single-resource editor validating the Phase 13 primitive
// extractions (Select × 2, Textarea × 1, .ui-form*). Mirrors the
// Phase 9 PATCH-diff lifecycle, scaled to 8 fields.

const TYPE_OPTIONS = [
  { value: "buyer", label: "Покупець" },
  { value: "seller", label: "Продавець" },
  { value: "both", label: "Покупець і продавець" },
];

const STATUS_OPTIONS = [
  { value: "lead", label: "Лід" },
  { value: "active", label: "Активний" },
  { value: "inactive", label: "Неактивний" },
  { value: "closed", label: "Закритий" },
];

const TYPE_VALUES = TYPE_OPTIONS.map((o) => o.value);
const STATUS_VALUES = STATUS_OPTIONS.map((o) => o.value);

const EDITABLE_KEYS = [
  "first_name",
  "last_name",
  "phone",
  "email",
  "type",
  "status",
  "source",
  "notes",
];

const FIRST_NAME_MAX = 128;
const LAST_NAME_MAX = 128;
const PHONE_MAX = 32;
const SOURCE_MAX = 128;
const NOTES_MAX = 2000;

const EMPTY_VALUES = EDITABLE_KEYS.reduce((acc, key) => {
  acc[key] = "";
  return acc;
}, {});

const extractEditable = (client) => {
  if (!client) return { ...EMPTY_VALUES };
  const next = {};
  for (const key of EDITABLE_KEYS) {
    next[key] = typeof client[key] === "string" ? client[key] : "";
  }
  return next;
};

const buildPatchBody = (values, baseline) => {
  const diff = {};
  for (const key of EDITABLE_KEYS) {
    if ((values[key] ?? "") !== (baseline[key] ?? "")) {
      diff[key] = values[key] ?? "";
    }
  }
  return diff;
};

const shallowEqualEditable = (a, b) => {
  if (!a || !b) return false;
  for (const key of EDITABLE_KEYS) {
    if ((a[key] ?? "") !== (b[key] ?? "")) return false;
  }
  return true;
};

const looksLikeEmail = (value) => /.+@.+\..+/.test(value);

// Feature-local validator. Explicit function, no schema engine.
const validateClient = (values) => {
  const errors = {};

  const firstName = (values.first_name ?? "").trim();
  if (firstName.length === 0) {
    errors.first_name = "Введіть ім'я.";
  } else if (firstName.length > FIRST_NAME_MAX) {
    errors.first_name = `Не довше ${FIRST_NAME_MAX} символів.`;
  }

  const lastName = values.last_name ?? "";
  if (lastName.length > LAST_NAME_MAX) {
    errors.last_name = `Не довше ${LAST_NAME_MAX} символів.`;
  }

  const phone = (values.phone ?? "").trim();
  if (phone.length === 0) {
    errors.phone = "Введіть телефон.";
  } else if (phone.length > PHONE_MAX) {
    errors.phone = `Не довше ${PHONE_MAX} символів.`;
  }

  const email = (values.email ?? "").trim();
  if (email && !looksLikeEmail(email)) {
    errors.email = "Введіть коректну електронну адресу.";
  }

  const type = values.type ?? "";
  if (!TYPE_VALUES.includes(type)) {
    errors.type = "Виберіть тип.";
  }

  const status = values.status ?? "";
  if (!STATUS_VALUES.includes(status)) {
    errors.status = "Виберіть статус.";
  }

  const source = values.source ?? "";
  if (source.length > SOURCE_MAX) {
    errors.source = `Не довше ${SOURCE_MAX} символів.`;
  }

  const notes = values.notes ?? "";
  if (notes.length > NOTES_MAX) {
    errors.notes = `Не довше ${NOTES_MAX} символів.`;
  }

  return errors;
};

const omitKey = (obj, key) => {
  if (!obj || obj[key] === undefined) return obj || {};
  const next = { ...obj };
  delete next[key];
  return next;
};

const omitKeys = (obj, keys) => {
  if (!obj) return obj || {};
  let next = obj;
  let mutated = false;
  for (const key of keys) {
    if (next[key] !== undefined) {
      if (!mutated) {
        next = { ...obj };
        mutated = true;
      }
      delete next[key];
    }
  }
  return next;
};

function ClientEditForm({ clientId, onSuccess }) {
  const [baseline, setBaseline] = useState(null);
  const [values, setValues] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);
  const [saveState, setSaveState] = useState("idle");
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientErrors, setClientErrors] = useState({});

  // Phase R3 — explicit stale-emission navigation. On successful
  // PATCH the form returns to the canonical client resource URL with
  // `location.state.mayBeStale: true`. The parent consumes the signal
  // once and clears it on its own mount-fetch success — refresh
  // ownership stays with the parent, no callback coordination here.
  const navigate = useNavigate();

  useEffect(() => {
    if (clientId == null) {
      setLoadState("not-found");
      return undefined;
    }

    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchClientById(clientId)
      .then((data) => {
        if (cancelled) return;
        setBaseline(data);
        setValues(extractEditable(data));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        if (status === 403) {
          setLoadState("forbidden");
        } else if (status === 404) {
          setLoadState("not-found");
        } else {
          setLoadError(messageFromError(err));
          setLoadState("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleValueChange = useCallback((name, value) => {
    setValues((prev) => (prev ? { ...prev, [name]: value } : prev));
    setClientErrors((prev) => omitKey(prev, name));
    setFieldErrors((prev) => omitKeys(prev, [name, "_form"]));
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  }, []);

  const dirty = useMemo(() => {
    if (!baseline || !values) return false;
    const baselineEditable = extractEditable(baseline);
    return !shallowEqualEditable(baselineEditable, values);
  }, [baseline, values]);

  const saving = saveState === "saving";
  const clientErrorsCount = Object.keys(clientErrors).length;
  const canSubmit = !saving && dirty && clientErrorsCount === 0;

  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    if (!values || !baseline || !dirty || saving) return;

    const clientErrs = validateClient(values);
    if (Object.keys(clientErrs).length > 0) {
      setClientErrors(clientErrs);
      setFieldErrors({});
      return;
    }

    setClientErrors({});
    setFieldErrors({});
    setSaveState("saving");

    try {
      const baselineEditable = extractEditable(baseline);
      const body = buildPatchBody(values, baselineEditable);
      const updated = await patchClientById(clientId, body);
      setBaseline(updated);
      setValues(extractEditable(updated));
      setSaveState("saved");
      // Embed-aware success path. When hosted inside a modal the
      // parent owns post-save lifecycle (close + refetch) — the form
      // announces success and the navigate-back branch is skipped so
      // mayBeStale is not double-emitted. Standalone route keeps the
      // navigate path so the existing /edit URL still recovers
      // freshness via the parent's mayBeStale consumer.
      if (onSuccess) {
        onSuccess(updated);
      } else {
        navigate(`/clients/${clientId}`, {
          state: {
            mayBeStale: true,
            reason: "Дані клієнта могли застаріти після редагування",
          },
        });
      }
    } catch (err) {
      setFieldErrors(parseDrfFieldErrors(err, EDITABLE_KEYS));
      setSaveState("error");
    }
  };

  const handleReset = () => {
    if (!baseline || saving) return;
    setValues(extractEditable(baseline));
    setClientErrors({});
    setFieldErrors({});
    setSaveState((prev) => (prev === "saved" ? "idle" : prev));
  };

  // ---------- Render gates ----------

  if (loadState === "loading") {
    return (
      <div className="feature-client-edit__state">
        <Spinner size="lg" label="Завантаження клієнта" />
      </div>
    );
  }

  if (loadState === "forbidden") {
    return (
      <div className="feature-client-edit__state">
        <EmptyState
          icon="fa-solid fa-lock"
          title="Немає доступу"
          description="Редагувати клієнта може лише власник або реалтор зі спільним доступом на редагування."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "not-found") {
    return (
      <div className="feature-client-edit__state">
        <EmptyState
          icon="fa-solid fa-magnifying-glass"
          title="Клієнта не знайдено"
          description="Перевірте адресу сторінки або поверніться до списку клієнтів."
          size="md"
        />
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div className="feature-client-edit__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити клієнта"
          description={loadError || "Спробуйте оновити сторінку."}
          size="md"
        />
      </div>
    );
  }

  if (!values) return null;

  const formError = fieldErrors._form;

  const errorOf = (name) => clientErrors[name] || fieldErrors[name];

  // ---------- Per-field handlers (closures over handleValueChange) ----------

  const onChange = (name) => (event) => handleValueChange(name, event.target.value);

  return (
    <form
      className="ui-form"
      data-feature="client-edit-form"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="ui-form__rows">
        <div className="feature-client-edit__pair">
          <Field
            id="client-edit-first_name"
            label="Ім'я"
            error={errorOf("first_name")}
            required
          >
            <TextInput
              name="first_name"
              type="text"
              value={values.first_name}
              onChange={onChange("first_name")}
              maxLength={FIRST_NAME_MAX}
              autoComplete="given-name"
              disabled={saving}
            />
          </Field>

          <Field
            id="client-edit-last_name"
            label="Прізвище"
            error={errorOf("last_name")}
          >
            <TextInput
              name="last_name"
              type="text"
              value={values.last_name}
              onChange={onChange("last_name")}
              maxLength={LAST_NAME_MAX}
              autoComplete="family-name"
              disabled={saving}
            />
          </Field>
        </div>

        <div className="feature-client-edit__pair">
          <Field
            id="client-edit-phone"
            label="Телефон"
            help="Унікальний у межах вашого облікового запису."
            error={errorOf("phone")}
            required
          >
            <TextInput
              name="phone"
              type="tel"
              value={values.phone}
              onChange={onChange("phone")}
              maxLength={PHONE_MAX}
              autoComplete="tel"
              placeholder="+380 …"
              disabled={saving}
            />
          </Field>

          <Field
            id="client-edit-email"
            label="Email"
            error={errorOf("email")}
          >
            <TextInput
              name="email"
              type="email"
              value={values.email}
              onChange={onChange("email")}
              autoComplete="email"
              placeholder="client@example.com"
              disabled={saving}
            />
          </Field>
        </div>

        <div className="feature-client-edit__pair">
          <Field
            id="client-edit-type"
            label="Тип"
            error={errorOf("type")}
            required
          >
            <Select
              name="type"
              value={values.type}
              onChange={onChange("type")}
              options={TYPE_OPTIONS}
              placeholder="Виберіть тип…"
              disabled={saving}
            />
          </Field>

          <Field
            id="client-edit-status"
            label="Статус"
            error={errorOf("status")}
            required
          >
            <Select
              name="status"
              value={values.status}
              onChange={onChange("status")}
              options={STATUS_OPTIONS}
              placeholder="Виберіть статус…"
              disabled={saving}
            />
          </Field>
        </div>

        <Field
          id="client-edit-source"
          label="Джерело"
          help="Звідки клієнт прийшов: рекомендація, реклама, тощо."
          error={errorOf("source")}
        >
          <TextInput
            name="source"
            type="text"
            value={values.source}
            onChange={onChange("source")}
            maxLength={SOURCE_MAX}
            autoComplete="off"
            disabled={saving}
          />
        </Field>

        <Field
          id="client-edit-notes"
          label="Нотатки"
          help={`До ${NOTES_MAX} символів. Простий текст.`}
          error={errorOf("notes")}
        >
          <Textarea
            name="notes"
            value={values.notes}
            onChange={onChange("notes")}
            rows={6}
            maxLength={NOTES_MAX}
            placeholder="Особисті нотатки про клієнта…"
            disabled={saving}
          />
        </Field>
      </div>

      <div className="ui-form__messages">
        {formError ? (
          <InlineError className="ui-form__message">{formError}</InlineError>
        ) : null}
        {saveState === "saved" && !dirty ? (
          <Text
            as="span"
            size="xs"
            tone="success"
            className="ui-form__saved"
          >
            Зміни збережено.
          </Text>
        ) : null}
      </div>

      <div className="ui-form__actions">
        <button
          type="button"
          className="ui-form__button ui-form__button--ghost"
          onClick={handleReset}
          disabled={!dirty || saving}
        >
          Скасувати зміни
        </button>
        <button
          type="submit"
          className="ui-form__button ui-form__button--primary"
          disabled={!canSubmit}
          data-loading={saving ? "true" : undefined}
        >
          {saving ? <Spinner size="sm" label="Збереження" /> : "Зберегти зміни"}
        </button>
      </div>
    </form>
  );
}

export default ClientEditForm;
